import { useMutation, useQuery, useQueryClient } from 'react-query';
import AsnAPI, { PrintRequestStatus, PrintRequest } from '../Api/asn';

export const useCancelAllPrintRequests = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (userId: string) => {
      const activePrintRequests = queryClient.getQueryData<Array<PrintRequest>>(
        ['print-requests-active', userId],
      );

      if (activePrintRequests) {
        const requestIds = activePrintRequests.map(
          (request) => request.request_id,
        );

        activePrintRequests.forEach((request) => {
          request.request_status = PrintRequestStatus.CANCELLED;
        });
        queryClient.setQueryData<Array<PrintRequest>>(
          ['print-requests-active', userId],
          [],
        );

        return AsnAPI.cancelPrintRequests(userId, requestIds);
      }
      return null;
    },
    {
      onError: (error) => {
        console.error(error);
      },
    },
  );
};

export const useUpdatePrintRequestStatus = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      userId,
      requestId,
      status,
      error,
    }: {
      userId: string;
      requestId: string;
      status: PrintRequestStatus;
      error?: string;
    }) => AsnAPI.updatePrintRequestStatus(userId, requestId, status, error),
    {
      onSuccess: (data, { userId, requestId, status }) => {
        queryClient.setQueryData<Array<PrintRequest>>(
          ['print-requests', userId],
          (oldData) => {
            if (!oldData) return [];
            const newData = [...oldData];
            const index = newData.findIndex(
              (request) => request.request_id === requestId,
            );

            if (index !== -1) {
              newData[index].request_status = status;
            } else {
              newData.push(data);
            }
            return newData;
          },
        );
        queryClient.setQueryData<Array<PrintRequest>>(
          ['print-requests-active', userId],
          (oldData) => {
            if (!oldData) return [];
            const newData = [...oldData];
            const index = newData.findIndex(
              (request) => request.request_id === requestId,
            );

            if (index !== -1) {
              newData[index].request_status = status;
            } else if (index === -1) {
              newData.push(data);
            }
            return newData;
          },
        );
      },
      onError: (error) => {
        console.error(error);
      },
    },
  );
};

export const useGetPrintRequests = (userId: string) =>
  useQuery(['print-requests', userId], () => AsnAPI.getPrintRequests(userId));

export const useGetActivePrintRequests = (userId: string) =>
  useQuery<PrintRequest[]>(['print-requests-active', userId], () =>
    AsnAPI.getPrintRequests(userId, true),
  );
